'use client';

import type { ComponentType, ReactNode } from 'react';
import {
  Arc,
  Atom,
  Blocks,
  CircularDots,
  Classic,
  Comet,
  Dual,
  Gather,
  Loading,
  Morph,
  Orbit,
  Radar,
  Slide,
  Snake,
  Swirl,
  Trace,
} from 'loading-dev';
import { Box, token } from '@terra/ui-styled-system';
import {
  CHAT_BODY_FONT_SIZE_PX,
  CHAT_STEP_LABEL_COLOR,
} from './chat-nesting-layout';
import type { ChatHeadlineKind } from './chat-headline-actions';
import './chat-headline-status.css';

type SpinnerProps = {
  size?: number;
  color?: string;
};

export type ChatHeadlineStatusState =
  | 'running'
  | 'completed'
  | 'stopped'
  | 'failed';

export type ChatHeadlineSpinnerSource = 'slide' | 'kind';

const KIND_HEADLINE_SPINNER: Record<
  ChatHeadlineKind,
  ComponentType<SpinnerProps>
> = {
  'use-skill': Swirl,
  'analyze-source': Loading,
  'analyze-data': Blocks,
  explore: Orbit,
  'search-web': Radar,
  recall: Atom,
  recon: CircularDots,
  operate: Trace,
  research: Comet,
  record: Gather,
  validate: Dual,
  'build-skill': Morph,
  'background-attack': Snake,
  'investigate-execution': Classic,
  passthrough: Arc,
};

const ICON_SIZE_PX = `${CHAT_BODY_FONT_SIZE_PX}px`;

export function headlineSpinnerForKind(
  kind: ChatHeadlineKind,
): ComponentType<SpinnerProps> {
  return KIND_HEADLINE_SPINNER[kind];
}

function HeadlineStatusSlot({ children }: { children: ReactNode }): ReactNode {
  return (
    <Box
      className="chat-headline-status-slot"
      width={ICON_SIZE_PX}
      height={ICON_SIZE_PX}
      color={CHAT_STEP_LABEL_COLOR}
      aria-hidden
    >
      {children}
    </Box>
  );
}

type TerminalStatusIconProps = {
  state: Exclude<ChatHeadlineStatusState, 'running'>;
  color: string;
};

function TerminalStatusIcon({
  state,
  color,
}: TerminalStatusIconProps): ReactNode {
  return (
    <svg
      className="chat-headline-status-icon"
      data-status={state}
      data-testid={
        state === 'completed'
          ? 'chat-headline-finished-icon'
          : state === 'stopped'
            ? 'chat-headline-stopped-icon'
            : 'chat-headline-failed-icon'
      }
      viewBox="0 0 16 16"
      width={CHAT_BODY_FONT_SIZE_PX}
      height={CHAT_BODY_FONT_SIZE_PX}
      fill="none"
    >
      <circle
        data-part="circle"
        cx="8"
        cy="8"
        r="6.5"
        stroke={color}
        strokeWidth="1.5"
        transform="rotate(-90 8 8)"
      />
      {state === 'completed' ? (
        <path
          data-part="mark"
          d="M4.75 8.15 L7.05 10.35 L11.35 5.7"
          stroke={color}
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      ) : null}
      {state === 'failed' || state === 'stopped' ? (
        <>
          <path
            data-part="mark"
            d="M5.5 5.5 L10.5 10.5"
            stroke={color}
            strokeWidth="1.5"
            strokeLinecap="round"
          />
          <path
            data-part="mark"
            d="M10.5 5.5 L5.5 10.5"
            stroke={color}
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </>
      ) : null}
    </svg>
  );
}

export function ChatHeadlineLoadingIcon({
  kind,
  spinnerSource = 'slide',
}: {
  kind: ChatHeadlineKind;
  spinnerSource?: ChatHeadlineSpinnerSource;
}): ReactNode {
  return (
    <ChatHeadlineStatusIcon
      kind={kind}
      state="running"
      spinnerSource={spinnerSource}
    />
  );
}

export function ChatHeadlineStatusIcon({
  kind,
  state,
  spinnerSource = 'slide',
}: {
  kind: ChatHeadlineKind;
  state: ChatHeadlineStatusState;
  spinnerSource?: ChatHeadlineSpinnerSource;
}): ReactNode {
  const Spinner =
    spinnerSource === 'kind' ? KIND_HEADLINE_SPINNER[kind] : Slide;
  const color = token('colors.fg.subtle');

  return (
    <HeadlineStatusSlot>
      {state === 'running' ? (
        <Spinner size={CHAT_BODY_FONT_SIZE_PX} color={color} />
      ) : (
        <TerminalStatusIcon state={state} color={color} />
      )}
    </HeadlineStatusSlot>
  );
}
